import { useMemo } from "react";
import { trpc } from "@/hooks/trpc";
import {
  AttributeKindEnum,
  type TestTemplateRevisionId,
} from "@brewbase/db/types";
import type {
  EditorConfig,
  TabularColumnInput,
  TabularColumnRow,
  TabularColumnsApi,
} from "@/features/template-block-editor/types";
import { renderTestRowFlags, renderTestTabularBadge } from "./row-flags-test";
import { TestInspectorExtras } from "./inspector-extras-test";
import { columnTextOptions } from "@brewbase/lib";
import { TEST_PHASE_SECTIONS } from "./phase-sections";
import { toast } from "sonner";

// Correction-mode editor config for test templates. Reads reuse revision-scoped
// queries (attributeListForRevision / tabular columns.list); writes go through
// tests.templateCorrections.* (lineage-preserving).

function useCorrectionTabularColumnsApi(
  workingId: TestTemplateRevisionId,
  templateAttributeId: number,
): TabularColumnsApi {
  const utils = trpc.useUtils();
  const attrId = templateAttributeId as never;

  const { data: columns, isLoading } =
    trpc.tests.testTemplateTabularColumns.list.useQuery(
      { templateAttributeId: attrId },
      { enabled: !!templateAttributeId },
    );

  const setColumnsMutation = trpc.tests.templateCorrections.setColumns.useMutation({
    // Column edits are written to the cache before saving, so a refused save
    // must say so: the refetch would otherwise silently undo the edit.
    onError: (err) => toast.error(`Column not saved: ${err.message}`),
    onSettled: () =>
      utils.tests.testTemplateTabularColumns.list.invalidate({ templateAttributeId: attrId }),
  });

  const replaceColumns = (cols: TabularColumnInput[]) =>
    setColumnsMutation.mutate({
      workingId,
      attrId: templateAttributeId,
      columns: cols.map((c) => ({
        lineageId: c.lineage_id != null ? String(c.lineage_id) : null,
        name: c.name,
        code: c.code,
        dataType: c.data_type as "number" | "string" | "datetime",
        isIndex: c.is_index,
        isComputed: c.is_computed,
        formula: c.formula,
        unit: c.unit,
        sortOrder: c.sort_order,
        textOptions: columnTextOptions(c),
      })),
    });

  const setColumnsCache = (cols: TabularColumnRow[]) =>
    utils.tests.testTemplateTabularColumns.list.setData(
      { templateAttributeId: attrId },
      cols as unknown as NonNullable<typeof columns>,
    );

  return { columns, isLoading, replaceColumns, setColumnsCache };
}

export function useTestCorrectionEditorConfig(
  workingId: TestTemplateRevisionId,
): EditorConfig {
  const utils = trpc.useUtils();

  const { data: attributesData } =
    trpc.tests.testTemplates.attributeListForRevision.useQuery(workingId);

  const invalidate = () =>
    utils.tests.testTemplates.attributeListForRevision.invalidate(workingId);

  const addAttr = trpc.tests.templateCorrections.addAttribute.useMutation({ onSettled: invalidate });
  const updateAttr = trpc.tests.templateCorrections.updateAttribute.useMutation({ onSettled: invalidate });
  const removeAttr = trpc.tests.templateCorrections.removeAttribute.useMutation({ onSettled: invalidate });
  const reorderAttr = trpc.tests.templateCorrections.reorderAttributes.useMutation({ onSettled: invalidate });

  const attributes = useMemo(
    () =>
      [...(attributesData ?? [])].sort(
        (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
      ),
    [attributesData],
  );

  return useMemo<EditorConfig>(
    () => ({
      templateId: workingId,
      isLocked: false,
      attributes,
      addAttribute: async (kind: AttributeKindEnum, section) => {
        const id = await addAttr.mutateAsync({ workingId, kind });
        if (section?.phase) {
          await updateAttr.mutateAsync({ workingId, attrId: id, settings: { phase: section.phase } });
        }
        return id;
      },
      removeAttribute: (id) => removeAttr.mutate({ workingId, attrId: id }),
      reorderAttributes: (ids) => reorderAttr.mutate({ workingId, attrIds: ids }),
      updateAttribute: (id, settings) =>
        updateAttr.mutate({ workingId, attrId: id, settings: normalize(settings) }),
      updateAttributeType: (id, updates) =>
        updateAttr.mutate({ workingId, attrId: id, settings: updates }),
      sections: TEST_PHASE_SECTIONS,
      formulaContextKind: "test-revision-attribute",
      tabularColumnFormulaContextKind: "test-tabular-column",
      useTabularColumnsApi: (templateAttributeId: number) =>
        useCorrectionTabularColumnsApi(workingId, templateAttributeId),
      renderRowFlags: renderTestRowFlags,
      renderTabularBadge: renderTestTabularBadge,
      renderInspectorExtras: (args) => <TestInspectorExtras {...args} />,
    }),
    [workingId, attributes, addAttr, updateAttr, removeAttr, reorderAttr],
  );
}

function normalize(settings: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...settings };
  for (const [k, v] of Object.entries(out)) {
    if (typeof v === "string" && v.trim() === "") out[k] = null;
  }
  return out;
}
